/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Callout, Tldr, FaqBlock, InlineCTA } from "./shared";

export const WhatIsAeoBody = ({ post, affiliateUrl, onTrack }) => (
  <>
    <p className="lead text-lg leading-relaxed text-white/80">
      Search isn't just a list of blue links anymore. When people ask{" "}
      <strong>ChatGPT</strong>, <strong>Perplexity</strong> or <strong>Google's AI Overviews</strong>{" "}
      a question, they get one answer — pulled, paraphrased and cited from the open web.{" "}
      <strong>AEO (Answer Engine Optimization)</strong> is how you make sure that answer is yours.
    </p>

    <Tldr>{post.tldr}</Tldr>

    <h2 id="definition">What does AEO mean?</h2>
    <p>
      AEO stands for <strong>Answer Engine Optimization</strong>. It's the discipline of
      writing and structuring web content so AI answer engines can extract a clean, quotable
      response from your page — and cite you as the source.
    </p>
    <p>
      Where classic SEO targets <em>ranking on a results page</em>, AEO targets being{" "}
      <em>quoted inside an AI-generated answer</em>. The skills overlap, but the formats are
      different.
    </p>

    <h2 id="aeo-vs-seo-vs-geo">AEO vs SEO vs GEO — quick comparison</h2>
    <div className="my-6 overflow-hidden rounded-2xl border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-wider text-white/45">
          <tr>
            <th className="p-3">Discipline</th>
            <th className="p-3">Scope</th>
            <th className="p-3">Primary signal</th>
            <th className="p-3">Outcome</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {[
            ["SEO", "Page-level", "Backlinks + on-page relevance", "Blue-link ranking on Google"],
            ["AEO", "Page-level", "Extractable answer blocks + schema", "Quoted by ChatGPT / Perplexity / AI Overviews"],
            ["GEO", "Brand / entity-level", "Schema + sameAs + unlinked mentions", "Named as the default brand in the category"],
          ].map((r) => (
            <tr key={r[0]}>
              {r.map((c, i) => (
                <td key={i} className={`p-3 ${i === 0 ? "font-display font-bold text-[#F09819]" : "text-white/80"}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <h2 id="why-aeo-matters">Why does AEO matter in 2026?</h2>
    <p>
      Generative answers are eating classic search. By late 2025, Gartner estimated that{" "}
      <strong>25% of category-defining queries</strong> were resolved by AI summaries before the
      user clicked a single result. That number is climbing fast.
    </p>
    <p>If you're not optimized for extraction, three things happen:</p>
    <ul>
      <li>Your competitors get cited instead of you</li>
      <li>Your organic CTR drops even when you rank #1 on Google</li>
      <li>Your brand never enters the AI's "trusted source" pool for your category</li>
    </ul>

    <h2 id="building-blocks">The 5 building blocks of AEO</h2>

    <h3>1. Question-style headings</h3>
    <p>
      Every H2 and H3 should be a question your buyer would type or speak aloud. AI engines
      extract answers from the first 2–3 sentences below a question. "What is AEO?" beats
      "Defining AEO" every time.
    </p>

    <h3>2. Self-contained answer blocks</h3>
    <p>
      Below each question, lead with a one-sentence answer. Then expand. Don't bury the lede —
      AI extractors only read the top of each section.
    </p>
    <Callout kind="do">
      <strong>Do:</strong> "What is AEO? AEO is the practice of formatting content so AI engines
      can extract and cite a clean answer from your page."
    </Callout>

    <h3>3. FAQ &amp; HowTo schema (JSON-LD)</h3>
    <p>
      Mark up your in-page Q&amp;A with <code>FAQPage</code> schema and step-by-step content with{" "}
      <code>HowTo</code> schema. These are the two formats AI engines lift{" "}
      <strong>verbatim</strong>.
    </p>

    <h3>4. Semantic content chunks</h3>
    <p>
      Use lists, tables, definition blocks, and short paragraphs. AI extractors prefer dense
      facts over flowing prose. A bulleted list of 5 items will be quoted more often than 200
      words of narrative covering the same ground.
    </p>

    <h3>5. An llms.txt manifest</h3>
    <p>
      Drop a <code>/llms.txt</code> file at your domain root listing your highest-value pages
      with short descriptions. It's the AI-era sitemap — and most of your competitors don't have
      one yet.
    </p>

    <h2 id="how-to-aeo">How to do AEO on an existing page in 6 steps</h2>
    {post.howto.steps.map((s, i) => (
      <div key={i} className="my-4">
        <h3 className="!mb-1">Step {i + 1} — {s.name}</h3>
        <p className="!mt-1">{s.text}</p>
      </div>
    ))}

    <InlineCTA
      post={post}
      location="midpost"
      affiliateUrl={affiliateUrl}
      onTrack={onTrack}
      label={{
        title: "Want every new page to ship AEO-ready by default?",
        sub: "Soro auto-injects FAQ/HowTo schema, writes in answer blocks, and publishes daily.",
        cta: "Try Soro free",
      }}
    />

    <h2 id="mistakes">3 AEO mistakes that kill citations</h2>
    <ol>
      <li><strong>Burying the answer</strong> under 400 words of throat-clearing intro.</li>
      <li><strong>Mismatched schema</strong> — claiming FAQPage but the questions don't appear in the visible content.</li>
      <li><strong>Blocking GPTBot</strong> in robots.txt because of a default plugin setting.</li>
    </ol>

    <FaqBlock faq={post.faq} />

    <h2 id="next">What's next</h2>
    <p>
      AEO is the page-level practice. The next layer up is <strong>GEO (Generative Engine
      Optimization)</strong> — making your whole brand recognizable to AI engines. Read our deep
      dive: <a href="/blog/geo-vs-seo">GEO vs SEO — what's the difference</a>.
    </p>
  </>
);
